import { Component, OnInit, ElementRef, ViewChild, forwardRef, Input, SimpleChanges, OnChanges, HostListener, HostBinding, Output, EventEmitter, Inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNil, set } from 'lodash-es';
import { Styles } from './models/style';
import { SelectorComponent } from './components/selector.component';
import { ESelectorType } from './components/selector.component';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { Locations_ShellService } from './Locations.shell.service';
import { Locations_OperationService } from './Locations.operation.service';
import { Locations_DatasourceService } from './Locations.datasource.index';
import { Locations_FlowService } from './Locations.flow.index';
import { Locations_ReportService } from './Locations.report.index';
import { Locations_LocalizationService } from './Locations.localization.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './Locations.frontend.types'
import { $frontendTypes as $types} from './Locations.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';


@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'Locations-locations_dd_single',
  templateUrl: './Locations.locations_dd_single.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Locations_locations_dd_singleComponent),
      multi: true
    }
  ]
})
export class Locations_locations_dd_singleComponent extends BaseComponent implements OnInit, ControlValueAccessor, OnChanges {

  @Input() type?: ESelectorType;
  _disabled = false;
  @Input() get disabled() { 
      return this._disabled 
      || this.$hasMissingRequiredInParams
      ; }
  set disabled(val: any) { this._disabled = val; }

  @Input() placeholder = '';
  @Input() styles: Styles;
  @Input() tooltip = '';

  @Output() displayTextChange: EventEmitter<string> = new EventEmitter();

  @ViewChild(SelectorComponent) selector: SelectorComponent;
  
  @HostBinding('tabIndex') get tabIndex() { return -1; }
  @HostListener('focus')
  focus() {
    this.selector?.focus();
  }

  get isMulti() {
    return false;
  }

  get isSingle() {
    return true;
  }


  inParams: { warehouseId: number, typeId: number, isLoose?: boolean, eligibleForAllocation?: boolean, parentId?: number } = { warehouseId: null, typeId: null, isLoose: null, eligibleForAllocation: null, parentId: null };
  @Input('warehouseId') set $inParams_warehouseId(value: any) {
    this.inParams['warehouseId'] = value;
  }
  get $inParams_warehouseId(): any {
    return this.inParams['warehouseId'] ;
  }
  @Input('typeId') set $inParams_typeId(value: any) {
    this.inParams['typeId'] = value;
  }
  get $inParams_typeId(): any {
    return this.inParams['typeId'] ;
  }
  @Input('isLoose') set $inParams_isLoose(value: any) {
    this.inParams['isLoose'] = value;
  }
  get $inParams_isLoose(): any {
    return this.inParams['isLoose'] ;
  }
  @Input('eligibleForAllocation') set $inParams_eligibleForAllocation(value: any) {
    this.inParams['eligibleForAllocation'] = value;
  }
  get $inParams_eligibleForAllocation(): any {
    return this.inParams['eligibleForAllocation'] ;
  }
  @Input('parentId') set $inParams_parentId(value: any) {
    this.inParams['parentId'] = value;
  }
  get $inParams_parentId(): any {
    return this.inParams['parentId'] ;
  }

  fullTextSearch: string;

  constructor(
    private utils: UtilsService,
    private settings: SettingsValuesService,
    private shell: Locations_ShellService,
    private datasources: Locations_DatasourceService,
    private flows: Locations_FlowService,
    private reports: Locations_ReportService,
    private localization: Locations_LocalizationService,
    private operations: Locations_OperationService,
    private logger: CleanupLoggerService,
    private _elementRef: ElementRef
    ) {
    super();
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.warehouseId)) {
        this.$missingRequiredInParams.push('warehouseId');
      }
      if(isNil(this.inParams.typeId)) {
        this.$missingRequiredInParams.push('typeId');
      }
  }
  
  async $init() {
    this.selector?.init();
  }

  onKeyDown($event: KeyboardEvent) {
    //TODO:KeyDown    
  }

  //ControlValueAccessor
  _innerValue: any;

  onChange: any = () => { };
  onTouch: any = () => { };

  get value(): number | number[] {
    return this._innerValue;
  }

  set value(val: number | number[]) {
    if (this._innerValue !== val) {
      this._innerValue = val;
      this.onChange(val);
      this.onTouch(val);
    }
  }

  writeValue(value: any): void {
    this._innerValue = value;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }



  displayWithFn(value: any): Promise<string> {
    const promise = new Promise<string>((resolve, reject) => {

      const $selector = this;
      const $utils = this.utils;

      this.fullTextSearch = null;
      const $fullTextSearch = null;
      this.datasources.Locations.ds_locations_dd.getByKeys({
        $keys: [value],
        warehouseId:  $selector.inParams.warehouseId ,
        typeId:  $selector.inParams.typeId ,
        fullTextSearch:  $selector.fullTextSearch ,
        isLoose:  $selector.inParams.isLoose ,
        eligibleForAllocation:  $selector.inParams.eligibleForAllocation ,
        parentId:  $selector.inParams.parentId ,
        locationId:  null ,
      }).then(result => {
        const found = result.result[0];
        const e: { Id?: number, EligibleForAllocation?: boolean, IsLoose?: boolean, Name?: string, TypeId?: number, WarehouseId?: number } = found ?? {};
        const $option = { entity: e };
         const $entity = e;
        const displayTextResult = found ? ($option.entity.Name) : 'No data to display';
        this.displayTextChange.emit(displayTextResult);
        resolve(displayTextResult);
      });

    });
    return promise;
  }

  optionsFn(filterText: string): Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }> {

    const promise = new Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }>((resolve, reject) => {

      const $selector = this;
      const $utils = this.utils;

      this.fullTextSearch = filterText;
      const $fullTextSearch = filterText;
      this.datasources.Locations.ds_locations_dd.getList({
        $top: 15,
        $skip: 0,
        warehouseId:  $selector.inParams.warehouseId ,
        typeId:  $selector.inParams.typeId ,
        fullTextSearch:  $selector.fullTextSearch ,
        isLoose:  $selector.inParams.isLoose ,
        eligibleForAllocation:  $selector.inParams.eligibleForAllocation ,
        parentId:  $selector.inParams.parentId ,
        locationId:  null ,
      }).then(result => {
        resolve({
          list: result.result.map(item => {
            const $option = { entity: item };
            const $entity = item;
            const $resultOption = { key: null, name: $option.entity.Name, disabled: null };
            $resultOption.key = $option.entity.Id;
            return $resultOption;
          }),
          totalCount: result.totalCount,
          top: 15
        });
      });

    });

    return promise;

  }

}
